import { Check, Edit, Trash2, X } from 'lucide-react';
import { Button, CircularProgress } from '@mui/material';
import { Dialog } from '../ui/Dialog';
import { useState } from 'react';
import { Client, Connection } from '@/types/api';
import { LiquidityScenarioHead } from '@/types/liquidity-api';
import { cn } from '@/lib/utils';
import { Input } from "@/components/ui/Input";
import { Body, H3 } from "@/components/ui/Typography";
import Card from '../ui/Card';
import { IconButton } from '../ui/IconButton';
import { theme } from '@/theme/theme';
interface LiquidityScenarioCardProps {
  scenarioHead: LiquidityScenarioHead;
  client?: Client,
  connection?: Connection,
  isActive?: boolean;
  isDeleting?: boolean;
  isSelected: boolean;
  onDelete: () => void;
  onSelect: () => void;
  onUpdateName: (newName: string) => void;
}

export const LiquidityScenarioCard = ({
  scenarioHead,
  isDeleting,
  isSelected,
  onDelete,
  onSelect,
  onUpdateName,
}: LiquidityScenarioCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(scenarioHead.name ?? "");

  const handleDelete = () => {
    setIsDeleteDialogOpen(false);
    onDelete();
  };

  const handleSaveClick = () => {
    onUpdateName(editedName);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(scenarioHead.name ?? "");
    setIsEditing(false);
  };

  if (isDeleting) {
    return (
      <Card>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <CircularProgress size={24} className="mb-2" />
            <p className="text-gray-600 text-sm">Deleting scenario...</p>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <>
      <Card
        className={cn(
          isSelected ? "border-border-dark" : "border-border-default",
          "cursor-pointer"
        )}
        onClick={onSelect}
      >
        <div className="grid grid-cols-2">
          <div className="flex-grow">
            {isEditing ? (
              <Input
                className="w-full p-2"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <H3>{scenarioHead.name}</H3>
            )}
            {!isEditing && <div className="mt-2">
              <Body size="small" className='text-palette-darkgray'>
                {new Date(scenarioHead.dateFrom).toLocaleDateString()} - {new Date(scenarioHead.dateTo).toLocaleDateString()}
              </Body>
            </div>}
          </div>

          <div className="flex gap-1 justify-end items-center">
            {isEditing ? (
              <>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveClick();
                  }}
                  color='success'
                  className="hover:bg-success hover:text-palette-white"
                >
                  <Check className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  className="hover:bg-error hover:text-palette-white"
                >
                  <X className="h-3.5 w-3.5" />
                </IconButton>
              </>
            ) : (
              <div className="flex gap-1 justify-end items-center">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="text-palette-gray hover:bg-background-default"
                >
                  <Edit className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(true);
                  }}
                  disabled={isDeleting}
                  className="text-palette-gray hover:bg-error hover:text-palette-white disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </Card >

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Radera scenario"
      >
        <div className="space-y-4">
          <Body>Är du säker på att du vill radera detta scenario? Denna åtgärd kan inte ångras.</Body>
          <div className="flex justify-end gap-2">
            <Button
              variant="text"
              color='primary'
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Avbryt
            </Button>
            <Button
              variant="text"
              color='error'
              onClick={handleDelete}
            >
              Radera
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};